import { useSearchParams } from 'react-router-dom';
import { COVER_CROP_CODES, HARVEST_DATE_CODES, SEED_DATE_CODES } from '../components/Field/constants/filters';
import { MAP_LAYERS } from '../constants/map-layers';
import { SCOPES } from '../constants/scopes';
import { CROP_TYPE_CODES } from './useCropTypes';
import { PRE_CROP_CODES } from './usePreCropTypes';
import { SOIL_TYPE_CODES } from './useSoilTypes';
export default function useSearchParamsWrapper() {
  const [searchParams, setSearchParams] = useSearchParams();

  function getSearchParams() {
    const params = {};
    for (const [key, value] of searchParams) {
      Object.assign(params, { [key]: value });
    }

    const areValidSearchParams = areValidParams(params);
    const hasSearchParams = Object.keys(params).length > 0;
    if (hasSearchParams && areValidSearchParams) {
      return params;
    } else {
      return getDefaultSearchParams();
    }
  }

  function getDefaultSearchParams() {
    const defaultParameters = {
      _northEastLng: 16.44,
      _southWestLng: 16.35,
      _northEastLat: 47.93,
      _southWestLat: 47.86,
      page: 1,
      cropType: CROP_TYPE_CODES.ALL,
      soilType: SOIL_TYPE_CODES.ALL,
      seedDate: SEED_DATE_CODES.ALL,
      harvestDate: HARVEST_DATE_CODES.ALL,
      coverCrop: COVER_CROP_CODES.ALL,
      preCrop: PRE_CROP_CODES.ALL,
      year: SCOPES[SCOPES.length - 1].value,
      mapLayer: MAP_LAYERS[0].settings.SATFARM_INTERNAL_ID,
    };
    return defaultParameters;
  }

  function areValidParams(params) {
    const VALID_PARAMS = {
      mapLayer: isValidMapLayer(params.mapLayer),
      cropType: isValidCropId(params.cropType),
      soilType: isValidSoilId(params.soilType),
      page: isValidPage(params.page),
      _northEastLng: isValidLng(params._northEastLng),
      _southWestLng: isValidLng(params._southWestLng),
      _northEastLat: isValidLat(params._northEastLat),
      _southWestLat: isValidLat(params._southWestLat),
      year: isValidYear(params.year),
    };

    return Object.keys(VALID_PARAMS).every((param) => VALID_PARAMS[param]);
  }

  function addSearchParams(newParams) {
    const areValidSearchParams = areValidParams(newParams);

    if (areValidSearchParams) {
      const params = getSearchParams();
      setSearchParams({ ...params, ...newParams });
    }
  }

  function isValidCropId(cropId) {
    const cropIdInt = parseInt(cropId);
    return cropIdInt === 0 || (cropIdInt > 104 && cropIdInt < 990);
  }

  // should validate against soiltypes endpoint,
  // but a few issues (fecth before auth, concurrent requests) need to be resolved before this is possible
  function isValidSoilId(soilType) {
    return soilType !== undefined;
  }

  function isValidYear(year) {
    return SCOPES.some(({ value }) => value === year);
  }

  function isValidPage(page) {
    return !isNaN(page);
  }

  function isValidMapLayer(mapLayer) {
    return MAP_LAYERS.some(({ settings: { SATFARM_INTERNAL_ID } }) => SATFARM_INTERNAL_ID === mapLayer);
  }

  function isValidLat(lat) {
    return lat >= -90 && lat <= 90;
  }

  function isValidLng(lng) {
    return lng >= -180 && lng <= 180;
  }

  return {
    getSearchParams,
    addSearchParams,
    getDefaultSearchParams,
  };
}
